/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
import { Button, Content, Flex } from "@adobe/react-spectrum";
import {
  and,
  Categorization,
  categorizationHasCategory,
  Category,
  isVisible,
  optionIs,
  RankedTester,
  rankWith,
  UISchemaElement,
  uiTypeIs,
} from "@jsonforms/core";
import {
  JsonFormsDispatch,
  useJsonForms,
  withJsonFormsLayoutProps,
} from "@jsonforms/react";
import Ajv from "ajv";
import Checkmark from "@spectrum-icons/workflow/Checkmark";
import { merge } from "lodash";
import React, { useEffect } from "react";
import SpectrumVerticalLayout from "@apollo/jsonforms-react-spectrum-renderers/src/layouts/SpectrumVerticalLayout";
import { SpectrumCategorizationRendererProps } from "@apollo/jsonforms-react-spectrum-renderers/src/complex/SpectrumCategorizationRenderer";
import "@apollo/jsonforms-react-spectrum-renderers/src/complex/SpectrumCategorizationWizardRenderer.css";
import StepList from "@apollo/jsonforms-react-spectrum-renderers/src/complex/StepList";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { setReduxState } from "../../redux/actions/SetReduxStateAction";

import {
  convertJsonSchemaPointerToDotNotation,
  convertToDotFormat,
  findAllObjectsWithKeyValue,
} from "../../utils/commonUtils";
import { FormContext } from "../../context/FormsContext";

export const wizardRendererTester: RankedTester = rankWith(
  4,
  and(
    uiTypeIs("Categorization"),
    categorizationHasCategory,
    optionIs("variant", "wizardDynamicForm")
  )
);

function WizardRenderer(props: SpectrumCategorizationRendererProps) {
  const getCategories = (): ReadonlyArray<Categorization | Category> => {
    const { data, uischema } = props;
    const ajv = new Ajv();
    const categorization = uischema as Categorization;
    return categorization.elements.filter((category) =>
      isVisible(category, data, "", ajv)
    );
  };

  const { renderers, cells, core } = useJsonForms();
  const errors = core?.errors || [];

  const { path, schema, config, uischema, visible } = props;
  const appliedUiSchemaOptions = merge({}, config, uischema.options);

  // Name of the context where wizard details will be stored
  const name = uischema?.options?.name as string;
  const [_, setFormContext] = React.useContext<any>(FormContext);
  const dispatch = useDispatch();
  const categories = getCategories();
  const jsonFormContext = useJsonForms();
  let nextButtonDisabled = false;

  const data = jsonFormContext.core?.data;

  const wizardCallback =
    jsonFormContext?.config?.lifecycleHooks?.onSuccessCallbacks
      ?.categorizationWizard;

  const onStepChangeCallback = wizardCallback?.onStepChangeCallback;

  // called only when the step changes. onStepChangeCallback is called on each render
  const onStepChange = wizardCallback?.onStepChange;

  // external validation callback
  const externalValidator = wizardCallback?.externalValidator;

  // Contains the schema for the progress indicator, eg: Step 1/4
  const progressIndicator = uischema?.options?.progressIndicator;

  // Properties for Previous and Next buttons
  const navigationButtons = uischema?.options?.navigationButtons;

  // Previous button props
  const previousButton = navigationButtons?.previousButton;

  // Next button props
  const nextButton = navigationButtons?.nextButton;

  // Properties of the button group surrounding the navigation buttons

  const buttonLayoutProps = navigationButtons?.layoutProps;

  // can be used when a back button needs to be given in the first
  // step and it will navigate to another page or perform some action
  const firstStepBackButton = uischema?.options?.firstStepBackButton;

  // Option to hide the back button in first step
  const hideFirstStepBackButton =
    uischema?.options?.hideFirstStepBackButton || false;

  // options for the Submit button for the wizard
  const submitButton = uischema?.options?.submitButton;

  // The context will store the step in 1,2,3, however the currentStep local variable needs it in the format 0,1,2
  // This is done for backward compatibility as currentStep local variable (0,1,2) was being sent to the callback hook
  // and the currentStep in the context needs to have the value 1,2,3 since will be used to display in the UI in
  // WizardProgress renderer.
  const currentStepVar = useSelector(
    (state: any) => state.reduxState?.[name]?.currentStep
  );
  const currentStep = (currentStepVar || 1) - 1;
  const reduxState = useSelector((state: any) => state.reduxState?.[name]);

  useEffect(() => {
    // Save the context data for the first time
    const step = uischema?.options?.currentStep || 0;
    const currentLabel = categories?.[step]?.label;
    if (!reduxState) {
      dispatch(
        setReduxState({
          [name]: {
            currentStep: step + 1,
            totalSteps: categories.length,
            currentLabel,
            progressIndicator,
          },
        })
      );
    }
  }, [uischema?.options?.currentStep, categories, name]);

  const setCurrentStep = (step: number) => {
    const currentLabel = categories?.[step]?.label;
    // If onStepChange exists, call it with the current step
    if (onStepChange) {
      onStepChange(step + 1);
    }
    dispatch(
      setReduxState({
        [name]: {
          ...reduxState,
          currentStep: step + 1,
          currentLabel,
        },
      })
    );
  };

  /**
   *
   * @param step step number
   * @returns returns boolean value based on  errors in current step
   */
  const checkStepErrors = (step: number) => {
    // Used to disable formTouched from external validator
    let showError = true;
    let errorExistsInCurrentStep = false;
    const elements = categories?.[step - 1]?.elements;

    const fieldsInCurrentStep = findAllObjectsWithKeyValue(
      elements,
      "type",
      "Control"
    );

    // Stores the field paths in dot notation
    const errorInstancePaths = [];

    // Loop through the errors and find which one is present in current step
    for (let i = 0; i < errors.length; i += 1) {
      const error = errors[i];

      let instancePath;

      if (error.keyword === "required") {
        if (error.instancePath) {
          instancePath = `${convertToDotFormat(error.instancePath)}.${
            error?.params?.missingProperty
          }`;
        } else {
          instancePath = error?.params?.missingProperty;
        }
      } else {
        instancePath = convertToDotFormat(error.instancePath) || path;
      }

      errorInstancePaths.push(instancePath);
    }

    // Loop though the fields in current step and identify if they are part of the error
    for (let i = 0; i < fieldsInCurrentStep.length; i += 1) {
      const field = fieldsInCurrentStep[i];
      const fieldDotNotation = convertJsonSchemaPointerToDotNotation(
        field?.scope
      );
      if (errorInstancePaths.includes(fieldDotNotation)) {
        errorExistsInCurrentStep = true;
        break;
      }
    }
    // apply external validator if defined
    if (externalValidator) {
      const { isValid, showError: externalShowError } = externalValidator(
        currentStep,
        core?.data || {}
      );
      showError = externalShowError;
      if (!isValid) {
        errorExistsInCurrentStep = true;
      }
    }

    return { hasError: errorExistsInCurrentStep, showError };
  };

  /**
   * Checks if there are any errors in the current set
   * If yes, sets the form as touched, displays the errors, and does not navigate to next page
   * If there are no errors then set the form touched as false (so that errors do not get displayed in the next step) and
   * navigates to the next page
   */
  const onNextButtonPress = async (step: number) => {
    const { hasError, showError } = checkStepErrors(step) || false;
    if (hasError) {
      // If there are errors then make the form as touched to show the errors
      setFormContext((oldValues: any) => ({
        ...oldValues,
        data,
        config: {
          ...oldValues?.config,
          formTouched: showError ?? true,
        },
      }));
    } else {
      const currentLabel = categories?.[step]?.label;

      // If onStepChange exists, call it with the current step
      if (onStepChange) {
        onStepChange(step + 1);
      }

      // If there are no errors, then set the form touched to false so that errors are not shown in the next step
      setFormContext((oldValues: any) => ({
        ...oldValues,
        data,
        config: {
          ...oldValues?.config,
          formTouched: false,
        },
      }));

      // Settimeout was added here so that the previous setContext completed and then only the navigation happens
      // Otherwise for a brief amount of time, errors will be shown in the next step
      setTimeout(() => {
        dispatch(
          setReduxState({
            [name]: {
              ...reduxState,
              currentStep: step + 1,
              currentLabel,
            },
          })
        );
      }, 0);
    }
  };

  if (onStepChangeCallback) {
    onStepChangeCallback({ setCurrentStep, currentStep });
  }
  /*  method to enable or disable next button based on option disableNextOnError  uichema */

  if (appliedUiSchemaOptions.disableNextOnError) {
    const { hasError } = checkStepErrors(currentStepVar);
    nextButtonDisabled = hasError;
  }

  return (
    <Flex
      direction="column"
      isHidden={!visible}
      UNSAFE_className="categorization-stepper"
      UNSAFE_style={uischema?.options?.style}
      data-testid="wizard-form"
    >
      <>
        <Flex justifyContent="center">
          {appliedUiSchemaOptions.showNavBar ? (
            <StepList>
              {categories.map((category, index) => (
                <div className="stepListItem" key={index}>
                  <div
                    className={`stepListMarker ${
                      index === currentStep ? "currentStep" : ""
                    } ${index > currentStep ? "nextStep" : ""}`}
                  >
                    <button
                      type="button"
                      tabIndex={appliedUiSchemaOptions.showNavButtons ? 0 : -1}
                      className="stepListButton"
                      onClick={() =>
                        appliedUiSchemaOptions.showNavButtons
                          ? setCurrentStep(index)
                          : {}
                      }
                    >
                      <div className="stepListIcon">
                        {index < currentStep && <Checkmark />}
                      </div>
                    </button>
                    {index !== 0 && (
                      <span
                        className={`stepListConnector ${
                          index > currentStep ? "disconnected" : ""
                        }`}
                      />
                    )}
                  </div>
                  <span
                    className={`stepListStepName ${
                      index > currentStep ? "gray" : ""
                    }`}
                  >
                    {category.label}
                  </span>
                </div>
              ))}
            </StepList>
          ) : (
            ""
          )}
        </Flex>
        {categories.map((category, index) => (
          <Content margin="size-160" isHidden={index !== currentStep}>
            {/* If the progress indicator is passed from outside, then it will be rendered using WizardProgress renderer.
   Show the below one by default if nothing is passed */}
            {!progressIndicator && (
              <Flex UNSAFE_style={category?.options?.style}>{`Step ${
                index + 1
              }/${categories.length}: ${category.label}`}</Flex>
            )}
            <SpectrumVerticalLayout
              uischema={
                {
                  type: "VerticalLayout",
                  elements: category.elements ?? [],
                } as UISchemaElement
              }
              schema={schema}
              path={path}
              direction="column"
              enabled
              visible
            />
          </Content>
        ))}
      </>

      {Boolean(appliedUiSchemaOptions.showNavButtons) || navigationButtons ? (
        // Flex is used here instead of button group since the buttons were getting mis aligned when
        // loader from submit button was being displayed
        <Flex
          gap="size-200"
          marginX="size-160"
          marginBottom="size-160"
          justifyContent="right"
          {...buttonLayoutProps}
        >
          {/* If Hide first back button is true then hide the back button */}
          {!(hideFirstStepBackButton && currentStep <= 0) &&
            // If there is a back button passed for the first step, render it

            (firstStepBackButton && currentStep <= 0 ? (
              <JsonFormsDispatch
                renderers={renderers}
                cells={cells}
                uischema={firstStepBackButton}
                schema={{}}
              />
            ) : (
              <Button
                variant="secondary"
                isDisabled={currentStep <= 0}
                onPress={() => setCurrentStep(currentStep - 1)}
                data-testid="previous-button"
                {...previousButton?.spectrumProps}
              >
                {previousButton?.label ?? "Previous"}
              </Button>
            ))}
          {/* If the submit button is provided, then show that in the last step */}
          {currentStep >= categories.length - 1 && submitButton ? (
            <JsonFormsDispatch
              renderers={renderers}
              cells={cells}
              uischema={submitButton}
              schema={{}}
            />
          ) : (
            <Button
              variant="primary"
              isDisabled={
                currentStep >= categories.length - 1 || nextButtonDisabled
              }
              onPress={() => onNextButtonPress(currentStep + 1)}
              data-testid="next-button"
              {...nextButton?.spectrumProps}
            >
              {nextButton?.label ?? "Next"}
            </Button>
          )}
        </Flex>
      ) : null}
    </Flex>
  );
}

export default withJsonFormsLayoutProps(WizardRenderer);
